import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { PAYMENT_MODE } from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  addSelectItem,
  beautify,
  createColumn,
  createEditAction,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_PAYMENT_MODES } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_DISTRICTS, API_PAYMENT_MODES } from "../../utils/apis";

export const usePaymentModes = () => {
  const tableColumns = () => [
    ...["mode", "description", "city_name", "status", "actions"].map((item) =>
      createColumn(
        item,
        item === "city_name" || item === "actions" ? false : true,
        beautify(item),
      ),
    ),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        city_name: Array.isArray(item.city_names)
          ? item.city_names.join(", ")
          : item.city_names,
        status: (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              item.active
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {item.active ? "Active" : "Inactive"}
          </span>
        ),
        actions: (
          <FormActions
            actions={[createEditAction(URL_PAYMENT_MODES, item?.id)]}
          />
        ),
      };
    });
  };

  const formRoute = getForm(URL_PAYMENT_MODES);
  const activityFilter = "payment_modes";

  return {
    tableData,
    tableColumns,
    formRoute,
    activityFilter,
  };
};

export const usePaymentModeForm = ({
  id,
  updateFormValues,
}: TFormHookParams = {}) => {
  // Get existing payment modes data
  const { loadData, loading, data } = useGet();
  const { loadData: getDistricts, data: districts } = useGet();

  useEffect(() => {
    loadData({ api: API_PAYMENT_MODES });
    getDistricts({ api: API_DISTRICTS });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: PAYMENT_MODE,
      });
    }
  }, [updateFormValues]);

  // Check if this is an edit operation (id exists)
  const isEdit = !!id;

  const sections: IFormSection[] = [
    {
      title: "Payment Mode Details",
      fields: [
        {
          field_name: "mode",
          label: "Payment Mode",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter payment mode (e.g., AIRTEL, MTN, CASH)",
            disabled: isEdit, // Disable the field when editing
          },
        },
        {
          field_name: "description",
          label: "Description",
          type: "textarea",
          props: {
            placeholder: "Enter description (e.g., AIRTEL collections)",
            rows: 3,
          },
        },
        {
          field_name: "active",
          label: "Status",
          type: "select",
          props: { required: true },
          options_data: [
            addSelectItem({ label: "Active", value: true }),
            addSelectItem({ label: "Inactive", value: false }),
          ],
        },
        {
          field_name: "city",
          label: "City",
          type: "select",
          props: { required: true, multiple: true },
          options_data: districts,
        },
      ],
    },
  ];

  const title = "Payment Mode Management";
  const redirect = getRegister(URL_PAYMENT_MODES);

  return {
    sections,
    title,
    redirect,
    loading,
    data,
  };
};

export const PaymentModeForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = usePaymentModeForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
